import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { Avatar, Field, Modal, Page, PageHead, Reveal } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';

function Toggle({ icon, title, sub, checked, onChange, disabled, badge }) {
  return (
    <label className={`setting ${disabled ? 'is-disabled' : ''}`}>
      <span className="setting__icon"><Icon name={icon} size={18} /></span>
      <span className="setting__text">
        <strong>{title} {badge && <span className="pill pill--gold pill--xs">{badge}</span>}</strong>
        <small className="muted">{sub}</small>
      </span>
      <input type="checkbox" className="switch" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function Choice({ icon, title, sub, value, options, onChange }) {
  return (
    <div className="setting setting--col">
      <div className="setting__row">
        <span className="setting__icon"><Icon name={icon} size={18} /></span>
        <span className="setting__text"><strong>{title}</strong><small className="muted">{sub}</small></span>
      </div>
      <div className="seg seg--icons">
        {options.map(([v, l]) => (
          <button key={v} type="button" className={value === v ? 'is-active' : ''} onClick={() => onChange(v)}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, setUser, isPremium, toast, logout } = useApp();
  const [pw, setPw] = useState({ current: '', next: '' });
  const [blocked, setBlocked] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const pv = user.privacy || {};

  useEffect(() => {
    api('/profiles/blocked').then((d) => setBlocked(d.items)).catch(() => {});
  }, []);

  const savePrivacy = async (patch) => {
    try {
      const r = await api('/user/me', { method: 'PUT', body: { privacy: { ...pv, ...patch } } });
      setUser(r.user);
      toast('Privacy updated');
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      const r = await api('/user/me/password', { method: 'PUT', body: pw });
      toast(r.message, 'success');
      setPw({ current: '', next: '' });
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const unblock = async (p) => {
    const r = await api(`/profiles/${p.profileId}/block`, { method: 'POST' });
    toast(r.message);
    setBlocked((b) => b.filter((x) => x.profileId !== p.profileId));
    setUser({ ...user, blocked: user.blocked.filter((x) => x !== p.profileId) });
  };

  const del = async () => {
    try {
      const r = await api('/user/me', { method: 'DELETE' });
      toast(r.message);
      logout();
      window.location.assign('/');
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  return (
    <Page>
      <div className="container narrow">
        <PageHead eyebrow="Settings" title="Privacy & account" sub="You decide what others see, and when." />

        <Reveal className="card settings-card">
          <h3 className="sub-h"><Icon name="lock" size={17} /> Privacy</h3>
          <Choice
            icon="image"
            title="Who can see my photos"
            sub="Free members see a locked placeholder when restricted."
            value={pv.photoVisibility || 'all'}
            options={[['all', 'All members'], ['premium', 'Premium members']]}
            onChange={(v) => savePrivacy({ photoVisibility: v })}
          />
          <Choice
            icon="phone"
            title="Who can see my contact details"
            sub="Phone and email on your profile."
            value={pv.showContact || 'premium'}
            options={[['premium', 'Premium members'], ['accepted', 'Accepted interests'], ['hidden', 'No one']]}
            onChange={(v) => savePrivacy({ showContact: v })}
          />
          <Toggle icon="sparkle" title="Show when I'm online" sub="Others see “Online now” and last active time." checked={pv.showOnline !== false} onChange={(v) => savePrivacy({ showOnline: v })} />
          <Toggle icon="eye" title="Incognito browsing" sub="View profiles without appearing in their visitors list." badge={!isPremium && 'Premium'} disabled={!isPremium} checked={pv.incognito} onChange={(v) => savePrivacy({ incognito: v })} />
          <p className="muted small"><Icon name="rupee" size={14} /> Income visibility is set on <Link to="/me/edit?tab=career">Edit profile → Career</Link>.</p>
        </Reveal>

        <Reveal className="card settings-card" delay={0.05}>
          <h3 className="sub-h"><Icon name="shield" size={17} /> Password</h3>
          {user.source === 'medhire' && <p className="muted small">You can always login with MedHire. Set a CTshaadi password to also login with email.</p>}
          <form onSubmit={changePw} className="row-2 align-end">
            <Field label="Current password"><input className="input" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder={user.source === 'medhire' ? 'Leave blank if not set' : ''} /></Field>
            <Field label="New password"><input className="input" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
            <button className="btn btn--primary">Update password</button>
          </form>
        </Reveal>

        <Reveal className="card settings-card" delay={0.1}>
          <h3 className="sub-h"><Icon name="block" size={17} /> Blocked profiles</h3>
          {!blocked.length ? <p className="muted small">You haven't blocked anyone.</p> : blocked.map((p) => (
            <motion.div key={p.profileId} layout className="blocked-row">
              <Avatar src={p.photo} name={p.name} size={40} />
              <span><strong>{p.name}</strong><small className="muted">{p.profileId}</small></span>
              <button className="btn btn--ghost btn--sm" onClick={() => unblock(p)}>Unblock</button>
            </motion.div>
          ))}
        </Reveal>

        <Reveal className="card settings-card danger-zone" delay={0.15}>
          <h3 className="sub-h"><Icon name="trash" size={17} /> Delete profile</h3>
          <p className="muted small">Found your match? Congratulations! Deleting removes your CTshaadi profile only{user.source === 'medhire' ? ' — your MedHire account is not affected' : ''}.</p>
          <button className="btn btn--danger" onClick={() => setConfirm(true)}>Delete my profile</button>
        </Reveal>
      </div>
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Delete your profile?">
        <div className="stack">
          <p>This hides your profile from everyone and ends your chats. This cannot be undone.</p>
          <div className="row-actions">
            <button className="btn btn--ghost" onClick={() => setConfirm(false)}>Keep my profile</button>
            <button className="btn btn--danger" onClick={del}>Yes, delete</button>
          </div>
        </div>
      </Modal>
    </Page>
  );
}
